import React, { useState, useId } from 'react';
import { cn } from '@/lib/utils';
import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface FloatingInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  optional?: boolean;
  error?: string;
  valid?: boolean;
  tooltip?: string;
}

export const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ label, optional, error, valid, tooltip, className, value, onFocus, onBlur, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const id = useId();
    const hasValue = value !== undefined && value !== '';
    const floated = focused || hasValue;

    return (
      <div className="space-y-1">
        <div className="relative">
          <input
            id={id}
            ref={ref}
            value={value}
            className={cn(
              'peer w-full h-12 px-4 pt-5 pb-1.5 rounded-xl border bg-background text-sm',
              'transition-all duration-200 outline-none',
              'focus:ring-2 focus:ring-ring focus:ring-offset-1',
              error && 'border-destructive focus:ring-destructive/30',
              valid && !error && 'border-success/50 focus:ring-success/30',
              !error && !valid && 'border-input',
              className
            )}
            onFocus={(e) => { setFocused(true); onFocus?.(e); }}
            onBlur={(e) => { setFocused(false); onBlur?.(e); }}
            {...props}
          />
          <label
            htmlFor={id}
            className={cn(
              'absolute left-4 transition-all duration-200 pointer-events-none select-none',
              floated
                ? 'top-1.5 text-xs font-semibold'
                : 'top-3.5 text-sm',
              error
                ? 'text-destructive'
                : focused
                  ? 'text-primary'
                  : 'text-muted-foreground'
            )}
          >
            {label}
            {optional && (
              <span className="ml-1 text-muted-foreground/60 font-normal">(opcional)</span>
            )}
            {!optional && <span className="ml-0.5 text-destructive">*</span>}
          </label>

          {/* Validation indicator */}
          {(error || valid) && (
            <div className={cn(
              'absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full',
              error ? 'bg-destructive' : 'bg-success'
            )} />
          )}

          {/* Tooltip */}
          {tooltip && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                    style={error || valid ? { right: '2rem' } : {}}
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px] text-xs">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Error message */}
        {error && (
          <p className="text-xs text-destructive pl-1 animate-fade-in">{error}</p>
        )}
      </div>
    );
  }
);

FloatingInput.displayName = 'FloatingInput';
